import { useEffect, useRef } from 'react';
import { warmupService, WarmupTarget } from '../services/warmupService';

/**
 * React hook for warming backend services when a component mounts.
 *
 * Fires a fire-and-forget warmup request after a small delay to avoid
 * competing with critical page load requests. Only fires once per mount
 * (uses useRef to prevent duplicate calls on re-renders).
 *
 * @param targets - Array of service targets to warm (e.g., ['collections', 'problems'])
 *
 * @example
 * // In HomePage
 * useWarmup(['collections', 'problems']);
 *
 * @example
 * // In CollectionPage
 * useWarmup(['collections', 'problems', 'chat']);
 */
export const useWarmup = (targets: WarmupTarget[]) => {
  const hasWarmed = useRef(false);

  useEffect(() => {
    // Only warm once per component mount
    if (hasWarmed.current) return;
    hasWarmed.current = true;

    // Small delay to not compete with critical page load requests
    const timer = setTimeout(() => {
      warmupService.warm(targets);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - we only want this to run once on mount
};
